import { useState } from 'react';
import { captureEvent } from '../../lib/telemetry';

/**
 * Banner de early access — exibido quando o tenant está em fase
 * de demo paga inicial. Convida o owner a (a) agendar onboarding
 * via Calendly, (b) mandar feedback direto.
 *
 * Quando aparece:
 *   - VITE_CALENDLY_URL está setado (config-driven, sem env = invisível).
 *   - Usuário ainda não dispensou (localStorage `ea_dismissed_at`).
 *
 * Por que persist em localStorage e não em DB:
 *   - Decisão é por dispositivo do usuário, não por tenant.
 *   - Zero round-trip — banner aparece/some sem flash.
 *   - Aceitável que reaparece em outro device — usuário dispensa de novo.
 *
 * Telemetria:
 *   - early_access_banner_view (1 por session)
 *   - early_access_cta_calendly (click no agendamento)
 *   - early_access_cta_feedback (click no feedback)
 *   - early_access_dismissed (X)
 */

const DISMISS_KEY = 'dentalflow.early_access_dismissed_at';

function shouldShow(): boolean {
  const calendly = import.meta.env.VITE_CALENDLY_URL as string | undefined;
  if (!calendly) return false;
  try {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (!ts) return true;
    // Re-mostra após 30 dias — caso o early access dure mais que isso
    // e o owner tenha esquecido que pode agendar onboarding.
    const dismissedAt = Number(ts);
    if (!Number.isFinite(dismissedAt)) return true;
    const daysSince = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
    return daysSince > 30;
  } catch {
    return true;
  }
}

export function EarlyAccessBanner() {
  const [visible, setVisible] = useState(() => shouldShow());

  if (!visible) return null;

  const calendlyUrl = import.meta.env.VITE_CALENDLY_URL as string | undefined;
  const feedbackEmail =
    (import.meta.env.VITE_FEEDBACK_EMAIL as string | undefined) ??
    'oi@dentalflow.com.br';
  const feedbackFormUrl = import.meta.env.VITE_FEEDBACK_FORM_URL as
    | string
    | undefined;

  const onCalendly = () => {
    captureEvent('early_access_cta_calendly', { url: calendlyUrl });
    window.open(calendlyUrl, '_blank', 'noopener,noreferrer');
  };

  const onFeedback = () => {
    if (feedbackFormUrl) {
      captureEvent('early_access_cta_feedback', { method: 'form' });
      window.open(feedbackFormUrl, '_blank', 'noopener,noreferrer');
    } else {
      captureEvent('early_access_cta_feedback', { method: 'mailto' });
      const subject = encodeURIComponent('[Kevins Clínicas] Feedback');
      const body = encodeURIComponent(
        'Olá! Quero compartilhar o seguinte feedback sobre o Kevins Clínicas:\n\n',
      );
      window.location.href = `mailto:${feedbackEmail}?subject=${subject}&body=${body}`;
    }
  };

  const onDismiss = () => {
    captureEvent('early_access_dismissed');
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // localStorage indisponível (Safari private, etc.) — apenas
      // some até reload.
    }
    setVisible(false);
  };

  return (
    <div
      role="region"
      aria-label="Early access"
      data-testid="early-access-banner"
      className="relative mx-4 mt-4 rounded-xl border border-brand-200 bg-gradient-to-r from-brand-50 via-white to-accent-50 p-4 shadow-sm"
    >
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dispensar banner"
        data-testid="early-access-dismiss"
        className="absolute right-2 top-2 p-1 rounded-md text-ink-400 hover:bg-ink-100 hover:text-ink-700 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pr-8">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-brand-600 text-white">
              Early Access
            </span>
            <span className="text-xs text-ink-500">
              Você está moldando o Kevins Clínicas
            </span>
          </div>
          <p className="text-sm text-ink-700 leading-snug">
            Seu feedback vira features. Agende 20 min de onboarding ou
            mande sugestões direto pra equipe.
          </p>
        </div>

        <div className="flex gap-2 flex-wrap shrink-0">
          <button
            type="button"
            onClick={onCalendly}
            data-testid="early-access-cta-calendly"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-brand-600 text-white hover:bg-brand-700 hover:shadow-md hover:-translate-y-0.5 transition-all"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Agendar onboarding
          </button>
          <button
            type="button"
            onClick={onFeedback}
            data-testid="early-access-cta-feedback"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-brand-300 text-brand-700 bg-white hover:bg-brand-50 hover:border-brand-400 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            Mandar feedback
          </button>
        </div>
      </div>
    </div>
  );
}
